import { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, Eye, Trash2, ShieldAlert, Loader2, CheckCircle2 } from 'lucide-react';

import { usePermission } from '@/hooks/usePermission';
import { Permissions } from '@/constants/permissions';
import { cn } from '@/utils/cn';
import {
  demoResetApi,
  type ResetAreaInfo, type ResetPreview, type ResetResult,
} from '@/api/demoReset.api';

export const DemoResetPage = () => {
  const { hasPermission } = usePermission();
  const canReset = hasPermission(Permissions.ADMIN_COMPANY_DELETE);

  const [areas, setAreas] = useState<ResetAreaInfo[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [preview, setPreview] = useState<ResetPreview | null>(null);
  const [result, setResult] = useState<ResetResult | null>(null);
  const [confirm, setConfirm] = useState('');
  const [previewing, setPreviewing] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { demoResetApi.areas().then(setAreas).catch(() => {}); }, []);

  const toggle = (key: string) => {
    setPreview(null); setResult(null);
    setSelected(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const keys = () => [...selected];

  const runPreview = useCallback(async () => {
    setError(null); setResult(null); setPreviewing(true);
    try { setPreview(await demoResetApi.preview(keys())); }
    catch (e: any) { setError(e?.response?.data?.message ?? 'Preview failed.'); }
    finally { setPreviewing(false); }
  }, [selected]);

  const companyCode = preview?.companyCode ?? '';
  const canExecute = canReset && selected.size > 0 && preview && preview.grandTotal > 0
    && confirm.trim().toUpperCase() === companyCode.toUpperCase();

  const runExecute = async () => {
    if (!canExecute) return;
    setError(null); setExecuting(true);
    try {
      const res = await demoResetApi.execute(keys(), confirm.trim());
      setResult(res); setPreview(null); setConfirm(''); setSelected(new Set());
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Reset failed.');
    } finally { setExecuting(false); }
  };

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900 overflow-auto">
      <div className="px-6 py-5 max-w-5xl w-full mx-auto space-y-5">
        <div>
          <h1 className="text-xl font-semibold text-slate-900 dark:text-white flex items-center gap-2">
            <Trash2 size={20} className="text-red-500" /> Demo Reset
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
            Permanently delete this company's rows in the selected areas. Destructive and irreversible — for demo prep only.
          </p>
        </div>

        {/* Danger banner */}
        <div className="flex gap-3 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700/50 px-4 py-3">
          <ShieldAlert size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-800 dark:text-amber-300">
            <span className="font-semibold">Destructive operation — no undo.</span> Every row in the selected areas is
            permanently deleted for the current company. Security (users, roles, sessions) is never touched. Always
            <span className="font-semibold"> Preview</span> first.
          </div>
        </div>

        {!canReset && (
          <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 py-3 text-sm text-slate-500">
            You don't have permission to run a demo reset (requires platform-admin).
          </div>
        )}

        {/* 1. Pick areas */}
        <section className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5">
          <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-3">1. Pick the areas to wipe</h2>
          <div className="grid sm:grid-cols-2 gap-2.5">
            {areas.map(a => (
              <label key={a.key}
                className={cn('flex gap-3 rounded-lg border px-3 py-2.5 cursor-pointer transition-colors',
                  selected.has(a.key)
                    ? 'border-signal bg-signal/5'
                    : 'border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700/40')}>
                <input type="checkbox" checked={selected.has(a.key)} onChange={() => toggle(a.key)} disabled={!canReset}
                  className="mt-0.5 accent-signal" />
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-900 dark:text-white">{a.name}</div>
                  <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{a.description}</div>
                  <div className="text-[10px] text-slate-400 mt-0.5">{a.tables.length} table(s)</div>
                </div>
              </label>
            ))}
          </div>
          <div className="flex items-center gap-3 mt-4">
            <button onClick={runPreview} disabled={selected.size === 0 || previewing}
              className="px-3 py-2 text-sm font-medium border border-slate-300 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 disabled:opacity-40 flex items-center gap-1.5">
              {previewing ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} />} Preview impact
            </button>
            <span className="text-xs text-slate-400">Read-only — counts what would be deleted. No side effects.</span>
          </div>
        </section>

        {error && (
          <div className="rounded-lg border border-red-300 bg-red-50 dark:bg-red-900/20 dark:border-red-700/50 px-4 py-3 text-sm text-red-700 dark:text-red-300">{error}</div>
        )}

        {/* 2. Review impact */}
        {preview && (
          <section className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5">
            <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-200">2. Review the impact — {preview.companyCode}</h2>
            <div className="mt-2 mb-4">
              <span className="text-3xl font-bold text-red-600">{preview.grandTotal.toLocaleString()}</span>
              <span className="ml-2 text-xs font-semibold text-slate-500 uppercase tracking-wide">rows will be deleted</span>
            </div>
            <div className="space-y-2">
              {preview.areas.map(a => (
                <details key={a.areaKey} className="rounded-lg border border-slate-100 dark:border-slate-700">
                  <summary className="flex items-center justify-between px-3 py-2 cursor-pointer text-sm">
                    <span className="font-medium text-slate-800 dark:text-slate-200">{a.areaName}</span>
                    <span className="font-mono text-xs text-slate-500">{a.total.toLocaleString()}</span>
                  </summary>
                  <div className="px-3 pb-2">
                    {a.tables.length === 0
                      ? <div className="text-xs text-slate-400 py-1">Nothing to delete.</div>
                      : a.tables.map(t => (
                          <div key={t.table} className="flex justify-between text-xs py-0.5 font-mono text-slate-500 dark:text-slate-400">
                            <span>{t.table}</span><span>{t.count.toLocaleString()}</span>
                          </div>))}
                  </div>
                </details>
              ))}
            </div>

            {/* 3. Confirm + execute */}
            {canReset && preview.grandTotal > 0 && (
              <div className="mt-5 pt-4 border-t border-slate-200 dark:border-slate-700">
                <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200 flex items-center gap-1.5">
                  <AlertTriangle size={15} className="text-red-500" /> 3. Confirm
                </h3>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  Type the company code <span className="font-mono font-semibold text-slate-700 dark:text-slate-200">{preview.companyCode}</span> to enable the wipe.
                </p>
                <div className="flex items-center gap-2 mt-2">
                  <input value={confirm} onChange={e => setConfirm(e.target.value)} placeholder={preview.companyCode}
                    className="px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-red-400 w-48" />
                  <button onClick={runExecute} disabled={!canExecute || executing}
                    className="px-4 py-2 text-sm font-semibold text-white bg-red-600 hover:bg-red-500 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5">
                    {executing ? <><Loader2 size={14} className="animate-spin" /> Deleting…</> : <><Trash2 size={14} /> Delete permanently</>}
                  </button>
                </div>
              </div>
            )}
          </section>
        )}

        {/* Result */}
        {result && (
          <section className="rounded-xl border border-teal-300 bg-teal-50 dark:bg-teal-900/20 dark:border-teal-700/50 p-5">
            <h2 className="text-sm font-semibold text-teal-800 dark:text-teal-300 flex items-center gap-1.5">
              <CheckCircle2 size={16} /> Reset complete — {result.totalDeleted.toLocaleString()} row(s) deleted across {result.deleted.length} table(s)
            </h2>
            {result.blocked.length > 0 && (
              <div className="mt-3 text-xs text-amber-700 dark:text-amber-400">
                <div className="font-semibold">{result.blocked.length} table(s) could not be cleared (still referenced — include the related area too):</div>
                {result.blocked.map(b => <div key={b.table} className="font-mono mt-0.5">{b.table}</div>)}
              </div>
            )}
          </section>
        )}
      </div>
    </div>
  );
};
